import { useCallback, useEffect, useRef, useState } from 'react';

import { useT } from './i18n';
import { ROUTES, useRoute } from './lib/router';
import { createInitialState } from './domain/state';
import type {
  OwnerContact,
  ParcelDetails,
  ParcelSelection,
  SubmissionPayload,
  SubmissionErrorCode,
  WizardState,
  Zonare,
  StareTeren,
} from './domain/types';

import { MAX_ADDITIONAL_FILES } from './domain/types';
import {
  focusFirstError,
  parseDecimal,
  validateStep1,
  validateStep2,
  validateStep3,
  STEP2_FIELD_ORDER,
  STEP3_FIELD_ORDER,
  type Errors,
} from './lib/validation';
import { submitSubmission } from './lib/api';

import { TopoBackground } from './components/TopoBackground';
import { Assurance } from './components/Assurance';
import { Stepper } from './components/Stepper';
import { StepLocate } from './components/StepLocate';
import { StepDetails } from './components/StepDetails';
import { StepContact } from './components/StepContact';
import { Acknowledgement } from './components/Acknowledgement';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { ConsentBanner } from './components/ConsentBanner';
import { onConsentChange, readConsent, resetConsent } from './lib/consent';
import { META_EVENTS, isMetaConfigured, loadMetaPixel, trackMeta } from './lib/meta';
import { readProvenance } from './lib/provenance';
import './App.css';

const TOTAL_STEPS = 3;

/** Scroll target for the hero's call to action. */
const WIZARD_ANCHOR_ID = 'formular';

let fileKeySeed = 0;
const nextFileKey = () => `f${++fileKeySeed}`;

export function App() {
  const t = useT();
  const [route, navigate] = useRoute();

  const [state, setState] = useState<WizardState>(createInitialState);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<SubmissionErrorCode | null>(null);
  const [submitted, setSubmitted] = useState<{ id: string; email: string } | null>(null);

  /*
   * Which location fields hold OUR answer rather than the owner's.
   *
   * It lives HERE, not inside step 1, because step 1 unmounts on every move to step 2
   * and a ref inside it would reset — while the values it describes live in this
   * component's state and survive. That mismatch is what made a redrawn parcel keep the
   * previous parcel's county after a trip to step 2 and back.
   */
  const locationAutoRef = useRef({ judet: false, localitate: false });

  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  /*
   * A returning visitor who already agreed on an earlier visit: load without
   * asking again. A fresh decision is the card's job, not this one's. Anyone
   * who declined, or who has not answered, gets nothing loaded at all.
   */
  useEffect(() => {
    if (readConsent() === 'granted') loadMetaPixel();
  }, []);

  const setSelection = useCallback((selection: ParcelSelection) => {
    setState((prev) => ({ ...prev, selection }));
    // An error must not outlive its fix. Once the parcel exists — or the manual
    // fields hold what the message asked for — the red line goes immediately,
    // rather than lingering until the next press of Continue.
    setErrors((prev) => {
      let next = prev;
      if (selection.geojson && selection.areaSqm) next = stripKey(next, 'parcel');
      if (selection.areaSqm !== null && selection.areaSqm > 0) next = stripKey(next, 'manualArea');
      if (selection.localityGuess.trim() !== '') next = stripKey(next, 'manualLocality');
      return next;
    });
  }, []);

  /**
   * Switches step 1 between drawing on the map and typing the figures by hand.
   * It lives here rather than inside the step because its control sits in the
   * nav row, and `selection.method` is the single source of truth for which
   * route is open.
   *
   * Both directions clear the selection: the two routes capture the parcel in
   * incompatible ways, and carrying half of one into the other would submit a
   * number the owner never confirmed.
   */
  const toggleManual = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selection: {
        method: prev.selection.method === 'manual' ? 'draw' : 'manual',
        geojson: null,
        areaSqm: null,
        centroid: null,
        localityGuess: '',
      },
    }));
    setErrors({});
  }, []);

  const setDetails = useCallback((update: Partial<ParcelDetails>) => {
    setState((prev) => ({ ...prev, details: { ...prev.details, ...update } }));
  }, []);

  const setContact = useCallback((update: Partial<OwnerContact>) => {
    setState((prev) => ({ ...prev, contact: { ...prev.contact, ...update } }));
  }, []);

  /*
   * Warn before an accidental close. The owner may have spent ten minutes
   * drawing a parcel and attaching a scanned certificate, and none of that is
   * recoverable — by design, nothing is persisted client-side.
   */
  const hasEnteredData =
    state.selection.geojson !== null ||
    state.selection.areaSqm !== null ||
    state.files.certificate !== null ||
    state.contact.email.trim() !== '';

  useEffect(() => {
    if (!hasEnteredData || submitted) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasEnteredData, submitted]);

  /**
   * Bring the wizard to the top of the viewport.
   *
   * The one scroll target in the app: the hero's call to action and every step
   * change use it, so moving between steps lands the reader in exactly the
   * place the CTA hands them to. `scroll-margin-top` on the target clears the
   * fixed masthead, so the stepper is not left sitting under the floating card.
   *
   * Honours prefers-reduced-motion: a smooth scroll down a long page is exactly
   * the kind of movement that setting exists to stop.
   */
  const scrollToWizard = useCallback(() => {
    const target = document.getElementById(WIZARD_ANCHOR_ID);
    if (!target) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }, []);

  /*
   * Every step change scrolls to the wizard, NOT to the top of the document.
   * The hero is a page and a half of reading that the owner has already done;
   * throwing them back up to it on every "Continuați" made each step feel like
   * a fresh page load, and hid the step they had just moved to below the fold.
   */
  const goToStep = useCallback(
    (next: number) => {
      setStep(next);
      setErrors({});
      scrollToWizard();
    },
    [scrollToWizard],
  );

  /*
   * The hero's call to action. Separate from `scrollToWizard` because every step
   * change scrolls too, and only this one means "the reader decided to start".
   * Putting the event inside the scroll helper would report it three more times
   * per submission and make the top of the funnel look wider than it is.
   */
  const startFromHero = useCallback(() => {
    trackMeta(META_EVENTS.startedForm);
    scrollToWizard();
  }, [scrollToWizard]);

  /** Copy step 1's findings into the step 2 fields the first time we arrive. */
  const advanceFromStep1 = () => {
    const found = validateStep1(state.selection, t);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError(found, ['manualArea', 'manualLocality']);
      return;
    }

    setState((prev) => ({
      ...prev,
      details: {
        ...prev.details,
        areaSqm: prev.details.areaSqm || (prev.selection.areaSqm ? formatAreaForInput(prev.selection.areaSqm) : ''),
        localitate: prev.details.localitate || prev.selection.localityGuess,
      },
    }));

    /*
     * The campaign's optimisation target. Fires here rather than the moment the
     * polygon closes, for two reasons: this point is reached by BOTH routes,
     * drawn and manual, so the signal is not biased toward people who can work
     * a map; and it means the parcel passed validation and the owner chose to
     * carry on, which is a materially better predictor than an outline that got
     * abandoned. See lib/meta.ts.
     */
    trackMeta(META_EVENTS.parcelLocated, true);
    goToStep(2);
  };

  const advanceFromStep2 = () => {
    const found = validateStep2(state.details, state.files, t);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError(found, STEP2_FIELD_ORDER);
      return;
    }
    goToStep(3);
  };

  const addCertificate = (incoming: File[]) => {
    const file = incoming[0];
    if (!file) return;
    setState((prev) => ({ ...prev, files: { ...prev.files, certificate: { id: nextFileKey(), file } } }));
    setErrors((prev) => stripKey(prev, 'certificate'));
  };

  const addAdditional = (incoming: File[]) => {
    setState((prev) => ({
      ...prev,
      files: {
        ...prev.files,
        additional: [
          ...prev.files.additional,
          ...incoming.map((file) => ({ id: nextFileKey(), file })),
        ].slice(0, MAX_ADDITIONAL_FILES),
      },
    }));
    setErrors((prev) => stripKey(prev, 'additional'));
  };

  const submit = async () => {
    const found = validateStep3(state.contact, t);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError(found, STEP3_FIELD_ORDER);
      return;
    }

    // Re-check the earlier steps: the owner could have edited them and walked
    // forward again, and the server will reject anything malformed anyway.
    const earlier = { ...validateStep1(state.selection, t), ...validateStep2(state.details, state.files, t) };
    if (Object.keys(earlier).length > 0) {
      setErrors(earlier);
      setStep(2);
      window.setTimeout(() => focusFirstError(earlier, STEP2_FIELD_ORDER), 0);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const payload = buildPayload(state);

    try {
      const result = await submitSubmission(payload, state.files, controller.signal);
      if (result.ok) {
        /*
         * Only on a server-accepted submission. Firing when the button is
         * pressed would count every rate-limited retry and every rejected
         * payload as a lead, and the number that decides whether this campaign
         * continues has to mean one stored request.
         */
        trackMeta(META_EVENTS.lead);
        setSubmitted({ id: result.id, email: state.contact.email.trim() });
        return;
      }

      setSubmitError(result.code);
      if (result.fields) {
        setErrors(result.fields);
        // Server field errors belong to step 2's inputs; take the owner there.
        const step2Keys = STEP2_FIELD_ORDER as readonly string[];
        if (Object.keys(result.fields).some((k) => step2Keys.includes(k))) setStep(2);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setSubmitError('server');
    } finally {
      setSubmitting(false);
    }
  };

  if (route === ROUTES.privacy) {
    return (
      <Shell>
        <PrivacyPolicy onBack={() => navigate(ROUTES.wizard)} />
      </Shell>
    );
  }

  if (submitted) {
    return (
      <Shell>
        <Acknowledgement id={submitted.id} email={submitted.email} />
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="wizard__header">
        <h1>{t.app.title}</h1>
        <p className="lede">{t.app.intro}</p>

        {/*
          One call to action, in the site's glass pill, carrying a label and the
          quiet line that qualifies it. It scrolls rather than navigates: the
          form is already on this page, and the hero's only job is to hand the
          reader down to it.
        */}
        <button type="button" className="btn btn--hero" onClick={startFromHero}>
          {t.app.ctaLabel}
          <span className="btn__arrow" aria-hidden="true">
            →
          </span>
        </button>

        {/* Outside the pill, under it: a note about the button, not a second
            label competing inside it. */}
        <p className="wizard__cta-note">{t.app.ctaNote}</p>
      </header>

      {/*
        The scroll target. It wraps the stepper AND the step body so the reader
        lands on "01 Localizare" with its heading, not halfway into the map.
      */}
      <div className="wizard__anchor" id={WIZARD_ANCHOR_ID}>
        <Stepper
          current={step}
          total={TOTAL_STEPS}
          labels={[t.steps.locate, t.steps.details, t.steps.contact]}
          onNavigate={goToStep}
        />

        <div className="wizard__step">
        {step === 1 && (
          <StepLocate
            selection={state.selection}
            errors={errors}
            onSelectionChange={setSelection}
            autoFilledRef={locationAutoRef}
            details={state.details}
            onDetailsChange={setDetails}
          />
        )}

        {step === 2 && (
          <StepDetails
            details={state.details}
            files={state.files}
            errors={errors}
            selectionMethod={state.selection.method}
            onDetailsChange={setDetails}
            onAddCertificate={addCertificate}
            onRemoveCertificate={() =>
              setState((prev) => ({ ...prev, files: { ...prev.files, certificate: null } }))
            }
            onAddAdditional={addAdditional}
            onRemoveAdditional={(id) =>
              setState((prev) => ({
                ...prev,
                files: { ...prev.files, additional: prev.files.additional.filter((f) => f.id !== id) },
              }))
            }
          />
        )}

        {step === 3 && (
          <StepContact
            contact={state.contact}
            details={state.details}
            selection={state.selection}
            files={state.files}
            errors={errors}
            onContactChange={setContact}
            onGoAttachCertificate={() => {
              setStep(2);
              // Land on the upload control itself, not the top of the step —
              // the owner clicked a button that named one specific thing.
              window.setTimeout(() => {
                const el = document.querySelector<HTMLElement>('[data-field="certificate"]');
                el?.focus({ preventScroll: true });
                el?.closest('.file-input')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
              }, 0);
            }}
          />
        )}
        </div>
      </div>

      {submitError && (
        <div className="submit-error" role="alert">
          <h3>{t.submit.errorHeading}</h3>
          <p>{errorMessage(submitError, t)}</p>
        </div>
      )}

      <div className="wizard__nav">
        {/* Step 1 has nothing to go back to, so the left slot carries the
            alternate route instead — a real button, not a stray text link
            under the map. */}
        {step === 1 && (
          <button type="button" className="btn" onClick={toggleManual}>
            {state.selection.method === 'manual' ? t.step1.fallbackCancel : t.step1.fallbackToggle}
          </button>
        )}
        {step > 1 && (
          <button type="button" className="btn" onClick={() => goToStep(step - 1)} disabled={submitting}>
            {t.nav.back}
          </button>
        )}
        <div className="wizard__nav-spacer" />

        {step === 1 && (
          <button type="button" className="btn" onClick={advanceFromStep1}>
            {t.nav.continue}
          </button>
        )}
        {step === 2 && (
          <button type="button" className="btn" onClick={advanceFromStep2}>
            {t.nav.continue}
          </button>
        )}
        {step === 3 && (
          <button
            type="button"
            className="btn"
            onClick={() => void submit()}
            // Disabled only while the request is in flight, never as a gate.
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting && <span className="spinner" aria-hidden="true" />}
            {submitError ? t.submit.retry : t.nav.submit}
          </button>
        )}
      </div>

      {/*
        Three answers to the sceptic's three objections — who are you, why is
        it free, what does not happen — as a FAQ accordion copying the one on
        prqpartners.com. It sits AFTER the wizard, immediately above the
        footer: an owner who is ready to fill the form should meet the form,
        not a wall of reassurance, and an owner who hesitates scrolls past it
        looking for exactly this.
      */}
      <Assurance />
    </Shell>
  );
}

function stripKey(errors: Errors, key: string): Errors {
  if (!errors[key]) return errors;
  const next = { ...errors };
  delete next[key];
  return next;
}

/**
 * Prefill the area field with plain digits — "4500", not the grouped "4.500".
 * parseDecimal understands grouping if the owner types it, but seeding an
 * editable field with separators invites edits that change the value's meaning.
 */
function formatAreaForInput(sqm: number): string {
  return String(Math.round(sqm));
}

function errorMessage(code: SubmissionErrorCode, t: ReturnType<typeof useT>): string {
  switch (code) {
    case 'network':
      return t.submit.errorNetwork;
    case 'validation':
      return t.submit.errorValidation;
    case 'rate_limit':
      return t.submit.errorRateLimit;
    case 'too_large':
      return t.submit.errorTooLarge;
    default:
      return t.submit.errorGeneric;
  }
}

function buildPayload(state: WizardState): SubmissionPayload {
  const { selection, details, contact } = state;
  return {
    locale: 'ro',
    selectionMethod: selection.method,
    parcelGeojson: selection.method === 'draw' ? selection.geojson : null,
    areaSqm: parseDecimal(details.areaSqm) ?? 0,
    judet: details.judet,
    localitate: details.localitate.trim(),
    cut: parseDecimal(details.cut) ?? 0,
    pot: parseDecimal(details.pot) ?? 0,
    statutTeren: details.statutTeren === '' ? null : details.statutTeren,
    zonare: details.zonare as Zonare,
    zonareAlt: details.zonare === 'altul' ? details.zonareAlt.trim() : null,
    stareTeren: details.stareTeren as StareTeren,
    situatieJuridica: details.situatieJuridica === '' ? null : details.situatieJuridica,
    utilitati: details.utilitati,
    drum: details.drum === '' ? null : details.drum,
    intentieNote: details.intentieNote.trim(),
    ownerName: contact.name.trim(),
    ownerEmail: contact.email.trim(),
    ownerPhone: contact.phone.trim(),
    consentGiven: contact.consent,
    consentTimestamp: new Date().toISOString(),
    /*
     * Was `document.referrer`, which is near-useless for paid social: Meta's
     * in-app browser reports l.facebook.com at best and iOS often strips it
     * entirely, so every ad-driven submission looked the same as every other.
     * This carries the campaign parameters captured at load instead, and still
     * includes the referrer for organic visits. See lib/provenance.ts.
     */
    referrer: readProvenance(),
  };
}

function Shell({ children }: { children: React.ReactNode }) {
  const t = useT();
  const [, navigate] = useRoute();

  return (
    <>
      <TopoBackground />

      <a className="skip-link" href="#main">
        {t.a11y.skipToContent}
      </a>

      <header className="masthead">
        <div className="masthead__inner">
          {/*
            The wordmark and the tagline sit at opposite ends of the card, so the
            tagline is a sibling of the link rather than inside it — it labels the
            product, it is not a second way to navigate home.
          */}
          <a
            className="masthead__brand"
            href={ROUTES.wizard}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              navigate(ROUTES.wizard);
            }}
          >
            {/* A brand name is not a phrase to translate. */}
            <span className="masthead__wordmark" translate="no">
              {t.brand.wordmark}
            </span>
          </a>
          <span className="masthead__tagline">{t.brand.tagline}</span>
        </div>
      </header>

      <main id="main" className="shell">
        {children}
      </main>

      <footer className="colophon">
        <div className="colophon__inner">
          <p>{t.footer.rights(new Date().getFullYear())}</p>
          {/*
            Opens in a new tab. A privacy policy is something you check while
            doing something else — sending someone away from a half-filled
            form to read it, then expecting them to find their way back, is
            how a form gets abandoned. `noopener` because any target="_blank"
            without it hands the new page a reference to this one.
          */}
          <a href={ROUTES.privacy} target="_blank" rel="noopener noreferrer">
            {t.footer.privacy}
          </a>
          <ConsentReset />
        </div>
      </footer>

      {/* Fixed to the viewport, so its position in the tree only decides paint
          order. Last means it sits above the masthead if they ever meet. */}
      <ConsentBanner />
    </>
  );
}

/**
 * "Setări de măsurare" — reopens the consent card by clearing the stored answer.
 *
 * Renders only once a decision exists. While the card is still on screen this
 * would be a second control for the same thing, sitting two inches below it.
 */
function ConsentReset() {
  const t = useT();
  const [state, setState] = useState(() => readConsent());

  useEffect(() => onConsentChange(setState), []);

  if (!isMetaConfigured() || state === 'unset') return null;

  return (
    <button type="button" className="btn-link cookie-notice-reset" onClick={resetConsent}>
      {t.footer.consentReset}
    </button>
  );
}
